import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations } from './translations';

const STORAGE_KEY = 'techrabbit_lang';
const LanguageContext = createContext(null);

function detectBrowserLanguage() {
  if (typeof navigator === 'undefined') return 'en';
  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
    navigator.userLanguage,
  ].filter(Boolean);

  for (const raw of candidates) {
    const code = String(raw).toLowerCase().split('-')[0];
    if (code === 'es') return 'es';
    if (code === 'en') return 'en';
  }
  return 'en';
}

function resolveInitialLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'es') return saved;
  } catch {
    /* ignore */
  }
  return detectBrowserLanguage();
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(resolveInitialLanguage);

  const setLang = useCallback((next) => {
    const value = next === 'es' ? 'es' : 'en';
    setLangState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    const meta = translations[lang]?.meta;
    if (meta?.title) document.title = meta.title;
    const description = document.querySelector('meta[name="description"]');
    if (description && meta?.description) {
      description.setAttribute('content', meta.description);
    }
  }, [lang]);

  const t = useCallback(
    (path) => {
      const parts = path.split('.');
      let cur = translations[lang];
      for (const part of parts) {
        if (cur == null) return path;
        cur = cur[part];
      }
      return cur ?? path;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      dict: translations[lang],
    }),
    [lang, setLang, t]
  );

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
